/**
 * Date utility functions for handling timezone conversions
 * - UI displays dates in PST (Pacific Standard Time) or device local timezone
 * - Database stores dates in UTC
 */

/**
 * Get current date in PST timezone as YYYY-MM-DD string
 * PST is UTC-8 (or UTC-7 during PDT)
 */
export function getPSTDateString(): string {
  const now = new Date();
  // Convert to PST (UTC-8) or PDT (UTC-7)
  const pstOffset = -8 * 60; // PST is UTC-8 in minutes
  const pdtOffset = -7 * 60; // PDT is UTC-7 in minutes
  
  // Check if daylight saving time is in effect (roughly March-November)
  const month = now.getUTCMonth(); // 0-11
  const isDST = month >= 2 && month <= 9; // March (2) to October (9)
  const offset = isDST ? pdtOffset : pstOffset;
  
  // Create date in PST/PDT
  const pstDate = new Date(now.getTime() + (offset * 60 * 1000));
  
  // Format as YYYY-MM-DD
  const year = pstDate.getUTCFullYear();
  const monthStr = String(pstDate.getUTCMonth() + 1).padStart(2, '0');
  const dayStr = String(pstDate.getUTCDate()).padStart(2, '0');
  
  return `${year}-${monthStr}-${dayStr}`;
}

/**
 * Get current date in device local timezone as YYYY-MM-DD string
 */
export function getLocalDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Convert UTC date string to PST date string (YYYY-MM-DD)
 */
export function utcToPSTDateString(utcDateString: string): string {
  if (!utcDateString) return '';
  
  try {
    // Parse UTC date string (could be ISO format or YYYY-MM-DD)
    let date: Date;
    if (utcDateString.includes('T') || utcDateString.includes('Z')) {
      // ISO format with time
      date = new Date(utcDateString);
    } else {
      // YYYY-MM-DD format - treat as UTC midnight
      date = new Date(utcDateString + 'T00:00:00Z');
    }
    
    // Convert to PST (UTC-8) or PDT (UTC-7)
    const month = date.getUTCMonth();
    const isDST = month >= 2 && month <= 9;
    const offset = isDST ? -7 : -8;
    const pstDate = new Date(date.getTime() + (offset * 60 * 60 * 1000));
    
    const year = pstDate.getUTCFullYear();
    const monthStr = String(pstDate.getUTCMonth() + 1).padStart(2, '0');
    const dayStr = String(pstDate.getUTCDate()).padStart(2, '0');
    
    return `${year}-${monthStr}-${dayStr}`;
  } catch (e) {
    console.error('Error converting UTC to PST:', e);
    return utcDateString; // Return original if conversion fails
  }
}

/**
 * Convert UTC date string to local timezone date string (YYYY-MM-DD)
 * NOTE: For date-only fields (like date_of_birth), use parseDateOnly() instead
 * to avoid timezone shifts. This function is for datetime fields only.
 */
export function utcToLocalDateString(utcDateString: string): string {
  if (!utcDateString) return '';
  
  try {
    // If it's already a date-only string (YYYY-MM-DD), return as-is
    // Date-only fields should NOT be timezone-converted
    if (/^\d{4}-\d{2}-\d{2}$/.test(utcDateString)) {
      return utcDateString;
    }
    
    let date: Date;
    if (utcDateString.includes('T') || utcDateString.includes('Z')) {
      date = new Date(utcDateString);
    } else {
      // YYYY-MM-DD format - treat as UTC midnight
      date = new Date(utcDateString + 'T00:00:00Z');
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (e) {
    console.error('Error converting UTC to local:', e);
    return utcDateString;
  }
}

/**
 * Convert PST date string (YYYY-MM-DD) to UTC ISO string for database storage
 */
export function pstToUTCISOString(pstDateString: string): string {
  if (!pstDateString) return '';
  
  try {
    // Parse PST date (YYYY-MM-DD)
    const [year, month, day] = pstDateString.split('-').map(Number);
    
    // Create date in PST (treat as midnight PST)
    const monthNum = month - 1; // JavaScript months are 0-indexed
    const isDST = monthNum >= 2 && monthNum <= 9;
    const offset = isDST ? -7 : -8;
    
    // Create UTC date by subtracting the offset
    const utcDate = new Date(Date.UTC(year, monthNum, day, 0, 0, 0));
    utcDate.setUTCHours(utcDate.getUTCHours() - offset);
    
    return utcDate.toISOString();
  } catch (e) {
    console.error('Error converting PST to UTC:', e);
    return pstDateString;
  }
}

/**
 * Convert local date string (YYYY-MM-DD) to UTC ISO string for database storage
 */
export function localToUTCISOString(localDateString: string): string {
  if (!localDateString) return '';
  
  try {
    // Parse local date (YYYY-MM-DD)
    const [year, month, day] = localDateString.split('-').map(Number);
    
    // Create date in local timezone (treat as midnight local time)
    const localDate = new Date(year, month - 1, day, 0, 0, 0);
    
    // Convert to UTC ISO string
    return localDate.toISOString();
  } catch (e) {
    console.error('Error converting local to UTC:', e);
    return localDateString;
  }
}

/**
 * Parse a date string (YYYY-MM-DD) without timezone conversion
 * This prevents dates from shifting when displayed
 */
export function parseDateOnly(dateString: string): Date | null {
  if (!dateString) return null;
  
  try {
    // If it's already a date string in YYYY-MM-DD format, parse it directly
    const parts = dateString.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed
      const day = parseInt(parts[2], 10);
      
      // Create date in PST timezone to avoid shifting
      return new Date(year, month, day);
    }
    
    // Fallback to standard parsing
    return new Date(dateString);
  } catch (e) {
    console.error('Error parsing date:', e);
    return null;
  }
}

/**
 * Format date for display in PST timezone
 * Handles date-only strings (YYYY-MM-DD) without timezone conversion
 * Treats YYYY-MM-DD strings as PST dates (not UTC)
 */
export function formatDatePST(dateString: string | Date, options?: Intl.DateTimeFormatOptions): string {
  if (!dateString) return '';
  
  try {
    let date: Date;
    
    if (typeof dateString === 'string') {
      // Check if it's a date-only string (YYYY-MM-DD)
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        // Parse as PST date - create date string with PST timezone
        // Format: YYYY-MM-DDTHH:mm:ss-08:00 (PST) or -07:00 (PDT)
        const parts = dateString.split('-');
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const day = parseInt(parts[2], 10);
        
        // Determine if DST (roughly March-November)
        const isDST = month >= 3 && month <= 10;
        const offset = isDST ? '-07:00' : '-08:00';
        
        // Create ISO string with PST/PDT offset
        const isoString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T12:00:00${offset}`;
        date = new Date(isoString);
      } else {
        date = new Date(dateString);
      }
    } else {
      date = dateString;
    }
    
    // Format in PST timezone
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'America/Los_Angeles',
      ...options
    });
  } catch (e) {
    console.error('Error formatting date:', e);
    return '';
  }
}

/**
 * Format date for display in PST timezone (short format)
 */
export function formatDatePSTShort(dateString: string | Date): string {
  return formatDatePST(dateString, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Format date for display in PST timezone (long format with weekday)
 */
export function formatDatePSTLong(dateString: string | Date): string {
  return formatDatePST(dateString, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Format date for display in local timezone
 * Handles date-only strings (YYYY-MM-DD) without timezone conversion
 */
export function formatDateLocal(dateString: string | Date): string {
  if (!dateString) return '';
  
  try {
    let date: Date;
    
    if (typeof dateString === 'string') {
      // Check if it's a date-only string (YYYY-MM-DD) WITHOUT time component
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        // Parse as local date to avoid timezone shifts
        const parts = dateString.split('-');
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed
        const day = parseInt(parts[2], 10);
        date = new Date(year, month, day);
      } else {
        // Has time component - parse as UTC and convert to local
        date = new Date(dateString);
      }
    } else {
      date = dateString;
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (e) {
    console.error('Error formatting date:', e);
    return '';
  }
}

/**
 * Format datetime (with time component) for display in local timezone
 * Converts from UTC to user's local timezone
 * Use this for timestamp fields like created_at, signed_at
 */
export function formatDateTimeLocal(dateTimeString: string | Date): string {
  if (!dateTimeString) return '';
  
  try {
    // Parse the datetime string as UTC and convert to local
    const date = typeof dateTimeString === 'string' ? new Date(dateTimeString) : dateTimeString;
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (e) {
    console.error('Error formatting datetime:', e);
    return '';
  }
}

/**
 * Format time string (HH:MM:SS or HH:MM) for display in PST timezone
 * @param timeString - Time string in format "HH:MM:SS" or "HH:MM" (assumed to be in UTC from database)
 * @param dateString - Optional date string (YYYY-MM-DD) to combine with time for proper timezone conversion
 * @returns Formatted time string in PST (e.g., "06:21 PM")
 */
export function formatTimePST(timeString: string, dateString?: string): string {
  if (!timeString) return '-';
  
  try {
    // Parse the time string (format: "HH:MM:SS" or "HH:MM")
    const timeParts = timeString.split(':');
    if (timeParts.length < 2) return timeString;
    
    const hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);
    
    // Use provided date or today's date
    let date: Date;
    if (dateString) {
      // Combine date and time, treat as UTC
      const dateParts = dateString.split('-');
      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1;
        const day = parseInt(dateParts[2], 10);
        // Create UTC date
        date = new Date(Date.UTC(year, month, day, hours, minutes, 0));
      } else {
        date = new Date();
        date.setUTCHours(hours, minutes, 0, 0);
      }
    } else {
      // Use today's date
      const now = new Date();
      date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), hours, minutes, 0));
    }
    
    // Format in PST timezone
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Los_Angeles',
      hour12: true
    });
  } catch (e) {
    console.error('Error formatting time to PST:', e);
    return timeString;
  }
}







